/* eslint-disable no-unused-vars */
import React, { memo } from 'react';
import PropTypes from 'prop-types';
import { Button, Checkbox, Dropdown, Input, Menu, Space, Tooltip } from 'antd';
import { PlusOutlined, SettingOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { TABLE_SIZE, useTableGrid } from '@/contexts/tableGridContact';
import { useFilter } from '@/contexts/FilterContext';
import { useResponsive } from '@/contexts/ResponsiveContext';
import { Link } from 'react-router-dom';

//filter
import { TableFiltersProvider } from '@/components/TableFilters/contexts/TableFiltersContext';
import FilterButton from '@/components/TableFilters/components/FilterButton';

const { SubMenu } = Menu;

const TableMenu = () => {
  const { handleChange, size, visibleColumns, handleDeletedCols, toggleAllHidden, columns } = useTableGrid();
  const { t } = useTranslation('table');
  const someColumnHidden = visibleColumns.length > 0 && visibleColumns.length !== columns.length;
  const allVisible = visibleColumns.length === columns.length;

  const menu = (
    <Menu onClick={handleChange}>
      <SubMenu title={t('tableSize')}>
        <Menu.Item key={TABLE_SIZE.big}>
          <div className={'w-40'}>
            <Checkbox checked={size === TABLE_SIZE.big}> {t('big')}</Checkbox>
          </div>
        </Menu.Item>
        <Menu.Item key={TABLE_SIZE.middle}>
          <div className={'w-40'}>
            <Checkbox checked={size === TABLE_SIZE.middle}> {t('medium')}</Checkbox>
          </div>
        </Menu.Item>
        <Menu.Item key={TABLE_SIZE.small}>
          <div className={'w-40'}>
            <Checkbox checked={size === TABLE_SIZE.small}> {t('small')}</Checkbox>
          </div>
        </Menu.Item>
      </SubMenu>
      <SubMenu title={t('showHideColumns')}>
        <div className={'pt-1'}>
          <div className={'py-1 px-3 border-0 border-b border-solid border-gray-300 mb-3'}>
            <Space>
              <Checkbox
                checked={allVisible}
                indeterminate={someColumnHidden}
                onChange={() => toggleAllHidden(someColumnHidden ? true : !allVisible)}>{t('showColumns')}</Checkbox>
              <Button type={'link'} onClick={() => toggleAllHidden(true)} disabled={allVisible}>
                {t('reset')}
              </Button>
            </Space>
          </div>
          {
            columns.map(c => <div key={`col-${c.dataIndex}`} className={'py-1 px-3 hover:bg-item-hover-bg'}>
              <Checkbox checked={c.visible} onChange={() => handleDeletedCols(c.dataIndex)}>
                {c.title}
              </Checkbox>
            </div>)
          }
        </div>
      </SubMenu>
    </Menu>
  );

  return <div className={'hidden lg:block'}>
    <Dropdown overlay={menu} trigger={['click']}>
      <Tooltip title={t('tableSettings')}>
        <Button icon={<SettingOutlined/>} type={'text'}/>
      </Tooltip>
    </Dropdown>
  </div>;
};

const AddButton = ({ onAdd, addLabel }) => {
  return (<Button type="primary" onClick={onAdd} icon={<PlusOutlined/>}>
    <div className={'ml-2 hidden md:inline'}>{addLabel}</div>
  </Button>
  );
};
const AddButtonConfig = ({ useCreatePath, route, onAdd, addLabel }) => {
  if (useCreatePath && route)
    return <Link to={`${route}/create`}>
      <AddButton addLabel={addLabel}/>
    </Link>;
  if (onAdd)
    return <AddButton addLabel={addLabel} onAdd={onAdd}/>;
  return <></>;
};

const TableHeader = ({
  title, subTitle, canSearch, route, useCreatePath, addLabel, onAdd, searchPlaceholder, extras, hasTable, canAdd,
  translation, filters, onBack, withOutFilters, plus
}) => {
  const { setSearch } = useFilter();
  const { isMobile } = useResponsive();
  const { t } = useTranslation('table');

  return (
    <TableFiltersProvider filters={filters} withOutFilters={withOutFilters}>
      <div className={'flex flex-col md:flex-row md:justify-between md:items-center mb-5 min-h-12'}>
        <div className="flex justify-between">
          <div className="flex items-center">
            {onBack && <Button type="button" className="mr-4" onClick={onBack}>{t('back')}</Button>}
            <h1 className={'uppercase mb-0 whitespace-nowrap'}>{title}</h1>
            {subTitle}
          </div>
          {
            isMobile &&
            <FilterButton translation={translation}/>
          }
        </div>

        <div className="flex pt-4 md:pt-0">
          {
            !isMobile &&
            <div className="px-3">
              <FilterButton translation={translation}/>
            </div>
          }
          {canSearch && <Input.Search
            className="min-w-48 w-full md:w-auto"
            allowClear
            placeholder={searchPlaceholder || t('searchPlaceholder')}
            onSearch={setSearch}
            enterButton/>
          }
          {
            canAdd &&
            <div className="pl-3">
              <AddButtonConfig onAdd={onAdd} addLabel={addLabel} route={route} useCreatePath={useCreatePath}/>
            </div>
          }
          {extras}
          {hasTable && <TableMenu/>}
        </div>
      </div>
      {plus}
    </TableFiltersProvider>
  );
};

export default memo(TableHeader);

TableHeader.propTypes = {
  addLabel: PropTypes.string,
  addText: PropTypes.string,
  withOutFilters: PropTypes.bool,
  canSearch: PropTypes.bool,
  extras: PropTypes.node,
  filters: PropTypes.any,
  hasTable: PropTypes.bool,
  onBack: PropTypes.func,
  onAdd: PropTypes.func,
  route: PropTypes.string,
  searchPlaceholder: PropTypes.string,
  title: PropTypes.string,
  subTitle: PropTypes.any,
  translation: PropTypes.string,
  translationPrefix: PropTypes.string,
  canAdd: PropTypes.bool,
  plus: PropTypes.oneOfType([PropTypes.node, PropTypes.element]),
  useCreatePath: PropTypes.bool
};

TableHeader.defaultProps = {
  addText: 'Añadir'
};

AddButtonConfig.propTypes = {
  addLabel: PropTypes.string,
  onAdd: PropTypes.func,
  route: PropTypes.string,
  useCreatePath: PropTypes.bool
};

AddButton.propTypes = {
  addLabel: PropTypes.string,
  onAdd: PropTypes.func
};
